// Full library of powers used in the GROW · Power Map view. Static data
// ported verbatim from workshop snapshot (workshop/data/powerLibrary.js).
//
// Cluster legend: the six groups mirror genesis' authoritative taxonomy
// (CreativityElement -> ElementGroup): Re = Resilience, Ro = Result
// Orientation, Fo = Future Orientation, In = Inventiveness, Ts = Team Spirit,
// Co = Communication. Each group holds exactly five powers.
// `level` (0-100) is optional - only powers the user has measured carry it.
// `badge` (true) marks distinguished levels.

export type PowerTag = "Re" | "Ro" | "Fo" | "In" | "Ts" | "Co";

export interface PowerEntry {
  tag: PowerTag;
  abbr: string;
  /** German display name (domain vocabulary); i18n consumers use nameId. */
  name: string;
  // i18n message id for the localized power name (resolve via
  // intl.formatMessage). Names are domain vocabulary - see the `power.name.*`
  // bundle keys. Built as `power.name.<abbr>`.
  nameId: string;
  level?: number;
  badge?: boolean;
}

export const POWER_LIBRARY: readonly PowerEntry[] = [
  // Resilience
  { tag: "Re", abbr: "Psc", name: "Ausdauer", nameId: "power.name.Psc" },
  { tag: "Re", abbr: "Min", name: "Achtsamkeit", nameId: "power.name.Min" },
  { tag: "Re", abbr: "Ada", name: "Adaption", nameId: "power.name.Ada" },
  { tag: "Re", abbr: "Con", name: "Konzentration", nameId: "power.name.Con" },
  { tag: "Re", abbr: "Agg", name: "Aggression", nameId: "power.name.Agg" },

  // Result Orientation
  { tag: "Ro", abbr: "Int", name: "Intention", nameId: "power.name.Int", level: 20 },
  { tag: "Ro", abbr: "Dil", name: "Sorgfalt", nameId: "power.name.Dil" },
  { tag: "Ro", abbr: "Str", name: "Strategie", nameId: "power.name.Str" },
  { tag: "Ro", abbr: "Rpy", name: "Verantwortung", nameId: "power.name.Rpy" },
  { tag: "Ro", abbr: "Cha", name: "Charisma", nameId: "power.name.Cha" },

  // Future Orientation
  { tag: "Fo", abbr: "Cou", name: "Mut", nameId: "power.name.Cou" },
  { tag: "Fo", abbr: "Fai", name: "Glaube", nameId: "power.name.Fai", level: 60, badge: true },
  { tag: "Fo", abbr: "Ima", name: "Imagination", nameId: "power.name.Ima" },
  { tag: "Fo", abbr: "Abs", name: "Abstraktion", nameId: "power.name.Abs" },
  { tag: "Fo", abbr: "Pas", name: "Passion", nameId: "power.name.Pas" },

  // Inventiveness
  { tag: "In", abbr: "Cur", name: "Neugierde", nameId: "power.name.Cur" },
  { tag: "In", abbr: "Ins", name: "Inspiration", nameId: "power.name.Ins", level: 80, badge: true },
  { tag: "In", abbr: "Imp", name: "Improvisation", nameId: "power.name.Imp", level: 40 },
  { tag: "In", abbr: "Aut", name: "Autonomie", nameId: "power.name.Aut" },
  { tag: "In", abbr: "Inu", name: "Intuition", nameId: "power.name.Inu" },

  // Team Spirit
  // genesis element COOPERATION (id 45); product-renamed to "Toleranz" to avoid
  // clashing with the Team Spirit cluster (Kooperationsfähigkeit i.w.S.).
  { tag: "Ts", abbr: "Cop", name: "Toleranz", nameId: "power.name.Cop" },
  { tag: "Ts", abbr: "Emp", name: "Empathie", nameId: "power.name.Emp" },
  { tag: "Ts", abbr: "Hum", name: "Humor", nameId: "power.name.Hum" },
  { tag: "Ts", abbr: "Fid", name: "Treue", nameId: "power.name.Fid" },
  { tag: "Ts", abbr: "Fns", name: "Fairness", nameId: "power.name.Fns" },

  // Communication
  { tag: "Co", abbr: "Awe", name: "Selbstwahrnehmung", nameId: "power.name.Awe", level: 100, badge: true },
  { tag: "Co", abbr: "Per", name: "Wahrnehmung", nameId: "power.name.Per" },
  { tag: "Co", abbr: "Lan", name: "Sprache", nameId: "power.name.Lan" },
  { tag: "Co", abbr: "Exp", name: "Expression", nameId: "power.name.Exp" },
  { tag: "Co", abbr: "Joy", name: "Optimismus", nameId: "power.name.Joy" },
];

// i18n message ids for the six cluster labels (resolve via intl).
export const TAG_LABEL_IDS: Record<PowerTag, string> = {
  Re: "power.tag.Re",
  Ro: "power.tag.Ro",
  Fo: "power.tag.Fo",
  In: "power.tag.In",
  Ts: "power.tag.Ts",
  Co: "power.tag.Co",
};

// Stable display order of the six clusters (matches genesis / the Powers panel).
export const POWER_GROUPS: readonly PowerTag[] = ["Re", "Ro", "Fo", "In", "Ts", "Co"];

// Lookup index: 3-letter abbreviation → library entry. Shared name/tag
// source for all power tiles (Match · Save · Grow).
export const POWER_BY_ABBR: Record<string, PowerEntry> = Object.fromEntries(
  POWER_LIBRARY.map((p) => [p.abbr, p]),
);
